import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AgentV2ConversationDetail, AgentV2ConversationListItem } from '@magimp/shared';
import { listAgentV2Conversations, reopenAgentV2Conversation } from '../../api/agent/index.js';
import { EmptyState } from '../../ui/index.js';

export interface AgentConversationHistoryProps {
  selectedConversationId: string | undefined;
  refreshKey: number;
  onSelect: (conversation: AgentV2ConversationDetail) => void;
}

export function AgentConversationHistory({
  selectedConversationId,
  refreshKey,
  onSelect,
}: AgentConversationHistoryProps) {
  const { i18n, t } = useTranslation('agent');
  const [items, setItems] = useState<AgentV2ConversationListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listAgentV2Conversations()
      .then((response) => {
        if (!cancelled) setItems(response.items);
      })
      .catch(() => {
        if (!cancelled) setError(t('history.loadFailed'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey, t]);

  const openConversation = async (id: string) => {
    setOpeningId(id);
    setError(null);
    try {
      onSelect(await reopenAgentV2Conversation(id));
    } catch {
      setError(t('history.openFailed'));
    } finally {
      setOpeningId(null);
    }
  };

  return (
    <aside
      aria-label={t('history.regionLabel')}
      data-testid="agent-history"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--s-2)',
        minWidth: 0,
        minHeight: 0,
      }}
    >
      <h2 style={{ fontSize: 13, fontWeight: 600, margin: 0, color: 'var(--fg-2)' }}>
        {t('history.title')}
      </h2>
      {error ? (
        <div role="alert" style={{ color: 'var(--status-error-fg)', fontSize: 12 }}>
          {error}
        </div>
      ) : null}
      {loading ? (
        <div style={{ color: 'var(--fg-3)', fontSize: 12 }}>{t('history.loading')}</div>
      ) : items.length === 0 ? (
        <EmptyState icon="message-square" text={t('history.empty')} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-2)' }}>
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              data-testid="agent-history-item"
              aria-current={item.id === selectedConversationId ? 'true' : undefined}
              disabled={openingId !== null}
              onClick={() => void openConversation(item.id)}
              style={{
                textAlign: 'left',
                border: '1px solid var(--border-1)',
                borderRadius: 'var(--r-2)',
                background:
                  item.id === selectedConversationId ? 'var(--accent-1-subtle)' : 'var(--bg-1)',
                color: 'var(--fg-1)',
                padding: 'var(--s-2)',
                cursor: openingId === null ? 'pointer' : 'default',
              }}
            >
              <span style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>
                {item.title || t('history.untitled')}
              </span>
              {item.summary ? (
                <span style={{ display: 'block', fontSize: 12, color: 'var(--fg-2)' }}>
                  {item.summary}
                </span>
              ) : null}
              <span style={{ display: 'block', fontSize: 11, color: 'var(--fg-3)' }}>
                {formatHistoryDate(item.lastMessageAt ?? item.createdAt, i18n.language)}
              </span>
            </button>
          ))}
        </div>
      )}
    </aside>
  );
}

function formatHistoryDate(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(iso),
  );
}
